/* =============================================================
   ERRORS
   =============================================================
   PostgREST hands back the raw Postgres error. Showing that to
   someone curating berths is useless — `23503` means nothing,
   and the default text names constraints, not decisions.

   So every message here explains what the *database rule* was
   and what to do about it. The rules themselves live in
   supabase/migrations/; this file only translates them.
   ============================================================= */

import type { PostgrestError } from '@supabase/supabase-js'

export class DataError extends Error {
  readonly code: string
  constructor(message: string, code = 'unknown') {
    super(message)
    this.name = 'DataError'
    this.code = code
  }
}

/** What each table's constraints mean, in the words of the thing being edited. */
const BY_CODE: Record<string, (detail: string) => string> = {
  // profiles.company_id, berths.company_id and vessels.company_id are all
  // `on delete restrict` — deliberately, so that deleting a company cannot
  // silently take its people, its berths or its fleet with it.
  // bookings.berth_id and bookings.vessel_id are `on delete restrict` too: a
  // booking is a record of what was agreed and outlives a change of fleet.
  '23503': (detail) =>
    detail.includes('bookings')
      ? 'It has bookings, so it cannot be deleted.'
      : 'This company still has users, berths or vessels attached to it, so it cannot be deleted.',

  '23505': (detail) =>
    detail.includes('berth_rates_position')
      ? 'Two rules cannot share a position. Reload and try again.'
      : 'Something with these details already exists.',

  // bookings_no_overlap, and request_booking's own check before it.
  '23P01': () => 'That berth is already booked or blocked for part of this stay.',


  '23514': (detail) => {
    if (detail.includes('latitude')) return 'Latitude must be between -90 and 90.'
    if (detail.includes('longitude')) return 'Longitude must be between -180 and 180.'
    if (detail.includes('contact_email')) return 'Enter a valid contact email address.'
    // Named constraints, not bare column names: `max_length_m` on berths
    // contains `length_m`, and its rule is a 5 m floor, not this one.
    if (detail.includes('berths_response_hours_iff_manual'))
      return 'A manual berth needs a response time; an instant one must not have one.'
    if (detail.includes('berth_blocks_ends_after_start'))
      return 'The block must end after it starts.'
    if (/vessels_(length|width|draft)_m_check/.test(detail))
      return 'Every dimension must be greater than zero.'
    return 'One of the values is not allowed. Check the highlighted fields.'
  },

  // RLS said no: the row belongs to another company, or the session expired.
  '42501': () =>
    'You do not have permission to change that. It may belong to another company.',

  '23502': () => 'A required field is missing.',

  // numeric field overflow — a value too big for the column's precision, e.g.
  // a four-digit draft in vessels.draft_m, which is numeric(4,2).
  '22003': () => 'That number is too large for this field.',
}

export function toDataError(error: PostgrestError | null, fallback: string): DataError {
  if (!error) return new DataError(fallback)

  // The booking functions raise 22023 with a sentence already written for
  // people ("booking is confirmed, not requested") -- pass it through as-is.
  if (error.code === '22023' && error.message) return new DataError(error.message, error.code)

  const detail = `${error.message} ${error.details ?? ''}`.toLowerCase()
  const explain = BY_CODE[error.code]
  return new DataError(explain ? explain(detail) : error.message || fallback, error.code)
}

/** The message to show for anything a data call threw: ours, or a fallback. */
export function errorMessage(e: unknown, fallback: string): string {
  return e instanceof DataError ? e.message : fallback
}
